import React, { FC, useCallback, useEffect, useState } from "react";
import { isEmpty, uniq } from "lodash";

import {
  FiltersHeader,
  ReferencePickerPlugin,
} from "@adsk/acc-reference-picker";
import { useCurrentProjectId } from "@adsk/alloy-provider";
import { useLocale } from "@adsk/alloy-react-locale";
import Panel from "@adsk/alloy-react-panel";

import { clients } from "../..";
import { useData } from "../../components/AssetDataContext";
import CategoryFilter from "../../components/CategoryFilter";
import LocationFilter from "../../components/LocationFilter";
import StatusFilter from "../../components/StatusFilter";
import {
  AssetDTO,
  AssetWithCategoryAndStatus,
  CategoryDTO,
  StatusDTO,
  StatusSetDTO,
} from "../../models";

type AssetsFilterPanelProps = React.ComponentProps<
  NonNullable<ReferencePickerPlugin<AssetWithCategoryAndStatus>["FiltersPanel"]>
>;

const AssetsFilterPanel: FC<AssetsFilterPanelProps> = ({
  filters,
  setFilters,
  open,
  floatingPanel,
  onClose,
  hasFilters,
  clearFilters,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const [categories, setCategories] = useState<CategoryDTO[] | undefined>(
    undefined
  );
  const [statusSets, setStatusSets] = useState<StatusSetDTO[] | undefined>(
    undefined
  );
  const [includeSubcategories, setIncludeSubcategories] =
    useState<boolean>(true);
  const [includeSublocations, setIncludeSublocations] = useState<boolean>(true);
  const title = T.translate("FILTER_ENTITY", {
    entity: T.translate("ASSETS"),
  });
  const projectId = useCurrentProjectId();
  // The reference-picker doesn't directly know about the consumer product's
  // LD flags. `AssetDataContext` is the integration seam: consuming products
  // (Build, Docs, etc.) hand it the resolved `isPercentCompleteEnabled` via
  // their `DataProvider`, mirroring how `EditableStatusField` receives the
  // same flag for the asset detail panel inside the picker.
  const { isPercentCompleteEnabled } = useData();

  const fetchCategories = useCallback(async () => {
    if (!projectId) {
      return;
    }

    try {
      const categories = await clients.getCategoryClient(projectId).list();

      setCategories(categories || []);
    } catch (_error) {
      setCategories([]);
    }
  }, [projectId]);

  const fetchStatus = useCallback(async () => {
    if (!projectId) {
      return;
    }

    let statusSets: StatusSetDTO[] = [];
    let statusSetsResponse;
    let cursorState;

    while (!statusSetsResponse || cursorState) {
      try {
        statusSetsResponse = await clients
          .getStatusSetClient(projectId)
          .list(cursorState);

        cursorState = statusSetsResponse?.pagination.cursorState;
        statusSets = [...statusSets, ...statusSetsResponse.results];
      } catch (_error) {
        setStatusSets([]);
        break;
      }
    }

    setStatusSets(statusSets);
  }, [projectId]);

  const onClearCategorySelection = useCallback(() => {
    const newFilters = { ...filters };
    delete newFilters.categoryIds;

    setFilters(newFilters);
  }, [filters, setFilters]);

  const onSelectCategory = useCallback(
    (selectedIds: CategoryDTO["id"][]) => {
      if (!selectedIds.length) {
        onClearCategorySelection();
        return;
      }

      setFilters({
        ...filters,
        categoryIds: selectedIds,
        includeSubcategories,
      });
    },
    [includeSubcategories, filters, setFilters, onClearCategorySelection]
  );

  const onSelectIncludeSubcategories = useCallback((isSelected: boolean) => {
    setIncludeSubcategories(isSelected);
  }, []);

  const onSelectStatus = useCallback(
    (statusId: StatusDTO["id"]) => {
      setFilters({
        ...filters,
        statusIds: uniq([
          ...((filters.statusIds as StatusDTO["id"][]) || []),
          statusId,
        ]),
      });
    },
    [filters, setFilters]
  );

  const onRemoveStatus = useCallback(
    (statusIdsToRemove: StatusDTO["id"][]) => {
      const { statusIds: currentStatusIds, ...otherFilters } = filters;
      const statusIds = [...(currentStatusIds as StatusDTO["id"][])].filter(
        (statusId) => !statusIdsToRemove.includes(statusId)
      );

      const newFilters = {
        ...otherFilters,
        ...(!isEmpty(statusIds) && { statusIds }),
      };

      setFilters(newFilters);
    },
    [filters, setFilters]
  );

  const onSelectIncludeSublocations = useCallback((isSelected: boolean) => {
    setIncludeSublocations(isSelected);
  }, []);

  const onSelectLocation = useCallback(
    (locationId: AssetDTO["locationId"]) => {
      setFilters({
        ...filters,
        locationId,
        includeSublocations,
      });
    },
    [filters, setFilters, includeSublocations]
  );

  const onRemoveLocation = useCallback(() => {
    const newFilters = { ...filters };
    delete newFilters.locationId;
    setFilters(newFilters);
  }, [filters, setFilters]);

  useEffect(() => {
    fetchCategories();
    fetchStatus();
  }, [fetchCategories, fetchStatus, setFilters]);

  return (
    <Panel
      open={open}
      floating={floatingPanel}
      renderHeader={() => (
        <FiltersHeader
          floatingPanel={floatingPanel}
          onClose={onClose}
          title={title as string}
          hasFilters={hasFilters}
          clearFilters={clearFilters}
        />
      )}
    >
      <Panel.Body>
        {categories && (
          <CategoryFilter
            categories={categories}
            selectedCategories={filters.categoryIds as CategoryDTO["id"][]}
            onSelectCategory={onSelectCategory}
            includeSubcategories={includeSubcategories}
            onSelectIncludeSubcategories={onSelectIncludeSubcategories}
            onClearSelection={onClearCategorySelection}
          />
        )}
        {statusSets && (
          <StatusFilter
            statusIds={filters.statusIds as StatusDTO["id"][]}
            statusSets={statusSets.filter(({ isActive }) => isActive)}
            isPercentCompleteEnabled={isPercentCompleteEnabled}
            onSelect={onSelectStatus}
            onRemove={onRemoveStatus}
          />
        )}
        <LocationFilter
          includeSublocations={includeSublocations as boolean}
          onSelectIncludeSublocations={onSelectIncludeSublocations}
          locationId={filters.locationId as AssetDTO["locationId"]}
          onSelectLocation={onSelectLocation}
          onRemoveLocation={onRemoveLocation}
        />
      </Panel.Body>
    </Panel>
  );
};

export default AssetsFilterPanel;
